var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import SuggestionItem from './SuggestionItem';
import { usePopper } from 'react-popper';
import { useParcaContext } from '@parca/components';
var Suggestion = /** @class */ (function () {
    function Suggestion(type, typeahead, value) {
        this.type = type;
        this.typeahead = typeahead;
        this.value = value;
    }
    return Suggestion;
}());
export { Suggestion };
var Suggestions = /** @class */ (function () {
    function Suggestions() {
        this.literals = [];
        this.labelNames = [];
        this.labelValues = [];
    }
    return Suggestions;
}());
export { Suggestions };
var LoadingSpinner = function () {
    var Spinner = useParcaContext().loader;
    return _jsx("div", __assign({ className: "pt-2 pb-4" }, { children: Spinner }));
};
var SuggestionsList = function (_a) {
    var suggestions = _a.suggestions, applySuggestion = _a.applySuggestion, inputRef = _a.inputRef, runQuery = _a.runQuery, focusedInput = _a.focusedInput, isLabelNamesLoading = _a.isLabelNamesLoading, isLabelValuesLoading = _a.isLabelValuesLoading;
    var _b = useState(null), popperElement = _b[0], setPopperElement = _b[1];
    var _c = usePopper(inputRef, popperElement, {
        placement: 'bottom-start',
    }), styles = _c.styles, attributes = _c.attributes;
    var _d = useState(-1), highlightedSuggestionIndex = _d[0], setHighlightedSuggestionIndex = _d[1];
    var _e = useState(true), showSuggest = _e[0], setShowSuggest = _e[1];
    var suggestionsLength = suggestions.literals.length + suggestions.labelNames.length + suggestions.labelValues.length;
    var getSuggestion = useCallback(function (index) {
        if (index < suggestions.labelNames.length) {
            return suggestions.labelNames[index];
        }
        if (index < suggestions.labelNames.length + suggestions.literals.length) {
            return suggestions.literals[index - suggestions.labelNames.length];
        }
        return suggestions.labelValues[index - suggestions.labelNames.length - suggestions.literals.length];
    }, [suggestions]);
    var resetHighlight = useCallback(function () { return setHighlightedSuggestionIndex(-1); }, [setHighlightedSuggestionIndex]);
    var applyHighlightedSuggestion = useCallback(function () {
        applySuggestion(getSuggestion(highlightedSuggestionIndex));
        resetHighlight();
    }, [resetHighlight, applySuggestion, highlightedSuggestionIndex, getSuggestion]);
    var applySuggestionWithIndex = useCallback(function (index) {
        applySuggestion(getSuggestion(index));
        resetHighlight();
    }, [resetHighlight, applySuggestion, getSuggestion]);
    var highlightNext = useCallback(function () {
        var nextIndex = highlightedSuggestionIndex + 1;
        if (nextIndex === suggestionsLength) {
            resetHighlight();
            return;
        }
        setHighlightedSuggestionIndex(nextIndex);
    }, [highlightedSuggestionIndex, suggestionsLength, resetHighlight]);
    var highlightPrevious = useCallback(function () {
        if (highlightedSuggestionIndex === -1) {
            // Didn't select anything, so starting at the bottom.
            setHighlightedSuggestionIndex(suggestionsLength - 1);
            return;
        }
        setHighlightedSuggestionIndex(highlightedSuggestionIndex - 1);
    }, [highlightedSuggestionIndex, suggestionsLength]);
    var handleKeyPress = useCallback(function (event) {
        if (event.key === 'Enter') {
            // Disable new line in the text area
            event.preventDefault();
        }
        // If there is a highlighted suggestion and enter is hit, we complete
        // with the highlighted suggestion.
        if (highlightedSuggestionIndex >= 0 && event.key === 'Enter') {
            applyHighlightedSuggestion();
        }
        // If no suggestions is highlighted and we hit enter, we run the query,
        // and hide suggestions until another actions enables them again.
        if (highlightedSuggestionIndex === -1 && event.key === 'Enter') {
            setShowSuggest(false);
            runQuery();
            return;
        }
        setShowSuggest(true);
    }, [highlightedSuggestionIndex, applyHighlightedSuggestion, runQuery]);
    var handleKeyDown = useCallback(function (event) {
        // Don't need to handle any key interactions if no suggestions there.
        if (suggestionsLength === 0 || !['Tab', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
            return;
        }
        event.preventDefault();
        // Handle tabbing through suggestions.
        if (event.key === 'Tab' && suggestionsLength > 0) {
            event.preventDefault();
            if (event.shiftKey) {
                // Shift + tab goes up.
                highlightPrevious();
                return;
            }
            // Just tab goes down.
            highlightNext();
        }
        // Up arrow highlights previous suggestions.
        if (event.key === 'ArrowUp') {
            highlightPrevious();
        }
        // Down arrow highlights next suggestions.
        if (event.key === 'ArrowDown') {
            highlightNext();
        }
    }, [suggestionsLength, highlightNext, highlightPrevious]);
    useEffect(function () {
        if (inputRef == null) {
            return;
        }
        inputRef.addEventListener('keydown', handleKeyDown);
        inputRef.addEventListener('keypress', handleKeyPress);
        return function () {
            inputRef.removeEventListener('keydown', handleKeyDown);
            inputRef.removeEventListener('keypress', handleKeyPress);
        };
    }, [inputRef, highlightedSuggestionIndex, suggestions, handleKeyPress, handleKeyDown]);
    return (_jsx(_Fragment, { children: suggestionsLength > 0 && (_jsx("div", __assign({ ref: setPopperElement, style: __assign(__assign({}, styles.popper), { marginLeft: 0 }) }, attributes.popper, { className: "z-50" }, { children: _jsx(Transition, __assign({ show: focusedInput && showSuggest, as: Fragment, leave: "transition ease-in duration-100", leaveFrom: "opacity-100", leaveTo: "opacity-0" }, { children: _jsxs("div", __assign({ style: { width: inputRef === null || inputRef === void 0 ? void 0 : inputRef.offsetWidth }, className: "absolute z-10 max-h-[400px] mt-1 bg-gray-50 dark:bg-gray-900 shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" }, { children: [isLabelNamesLoading ? (_jsx(LoadingSpinner, {})) : (_jsx(_Fragment, { children: suggestions.labelNames.map(function (l, i) { return (_jsx(SuggestionItem, { isHighlighted: highlightedSuggestionIndex === i, onHighlight: function () { return setHighlightedSuggestionIndex(i); }, onApplySuggestion: function () { return applySuggestionWithIndex(i); }, onResetHighlight: function () { return resetHighlight(); }, value: l.value }, l.value)); }) })), suggestions.literals.map(function (l, i) { return (_jsx(SuggestionItem, { isHighlighted: highlightedSuggestionIndex === i + suggestions.labelNames.length, onHighlight: function () {
                                return setHighlightedSuggestionIndex(i + suggestions.labelNames.length);
                            }, onApplySuggestion: function () {
                                return applySuggestionWithIndex(i + suggestions.labelNames.length);
                            }, onResetHighlight: function () { return resetHighlight(); }, value: l.value }, l.value)); }), isLabelValuesLoading ? (_jsx(LoadingSpinner, {})) : (_jsx(_Fragment, { children: suggestions.labelValues.map(function (l, i) { return (_jsx(SuggestionItem, { isHighlighted: highlightedSuggestionIndex ===
                                    i + suggestions.labelNames.length + suggestions.literals.length, onHighlight: function () {
                                    return setHighlightedSuggestionIndex(i + suggestions.labelNames.length + suggestions.literals.length);
                                }, onApplySuggestion: function () {
                                    return applySuggestionWithIndex(i + suggestions.labelNames.length + suggestions.literals.length);
                                }, onResetHighlight: function () { return resetHighlight(); }, value: l.value }, l.value)); }) }))] })) })) }))) }));
};
export default SuggestionsList;
